import { useState } from 'react';
import { ProviderStatusList } from '../components/ProviderStatusList';
import { Alert, Button, ConfirmDialog, Field, Panel, inputClass } from '../components/ui';
import * as ops from '../domain/projectOps';
import { CURRENT_SCHEMA_VERSION, NAME_MAX } from '../domain/schemas';
import { downloadText } from '../lib/download';
import { useProject } from '../state/projectContext';
import { useServices } from '../state/services';
import { toStorageError } from '../storage/errors';
import { exportProject } from '../storage/importExport';

export function SettingsWorkspace({ onDeleted }: { onDeleted: () => void }) {
  const { project, update, session } = useProject();
  const { repo, audio } = useServices();
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInvalid = project.name.trim().length === 0;

  const onExport = async () => {
    setError(null);
    try {
      if (!(await session.saveNow())) throw new Error('Save failed; export cancelled.');
      const { fileName, text } = await exportProject(repo, project.id);
      downloadText(fileName, text);
    } catch (e) {
      setError(toStorageError(e, 'Export failed.').message);
    }
  };

  const onDelete = async () => {
    setConfirm(false);
    try {
      await audio.stop();
      await repo.delete(project.id);
      onDeleted();
    } catch (e) {
      setError(toStorageError(e, 'Delete failed.').message);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {error && <Alert title={error} />}
      <Panel title="Project">
        <div className="grid gap-4">
          <Field label="Name" htmlFor="project-name" hint={nameInvalid ? undefined : `Up to ${NAME_MAX} characters.`}>
            <input
              id="project-name"
              className={inputClass}
              value={project.name}
              maxLength={NAME_MAX}
              aria-invalid={nameInvalid || undefined}
              aria-describedby={nameInvalid ? 'project-name-error' : undefined}
              onChange={(e) => update((p) => ops.renameProject(p, e.target.value))}
            />
          </Field>
          {nameInvalid && (
            <p id="project-name-error" role="alert" className="-mt-3 text-xs text-red-400">
              A name is required; changes will not save until one is entered.
            </p>
          )}
          <Field label="Description" htmlFor="project-description">
            <textarea
              id="project-description"
              className={`${inputClass} min-h-24`}
              value={project.description}
              maxLength={2000}
              onChange={(e) => update((p) => ops.setDescription(p, e.target.value))}
            />
          </Field>
          <dl className="grid grid-cols-2 gap-2 text-xs text-zinc-400 sm:grid-cols-4">
            <div>
              <dt>Schema</dt>
              <dd className="text-zinc-200">v{CURRENT_SCHEMA_VERSION}</dd>
            </div>
            <div>
              <dt>Tracks</dt>
              <dd className="text-zinc-200">{project.tracks.length}</dd>
            </div>
            <div>
              <dt>Assets</dt>
              <dd className="text-zinc-200">{project.assets.length}</dd>
            </div>
            <div>
              <dt>Generations</dt>
              <dd className="text-zinc-200">{project.generationRequests.length}</dd>
            </div>
          </dl>
        </div>
      </Panel>

      <Panel title="Generation providers">
        <p className="mb-2 text-sm text-zinc-400">
          Phase 0 has no AI providers and needs no API keys. Only the local, rule-based pattern generator is active.
        </p>
        <ProviderStatusList />
      </Panel>

      <Panel title="Data">
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void onExport()}>Export project file</Button>
          <Button variant="danger" onClick={() => setConfirm(true)}>
            Delete project…
          </Button>
        </div>
      </Panel>

      <ConfirmDialog
        open={confirm}
        title="Delete this project?"
        body={<>“{project.name}” and its local files will be permanently removed from this browser.</>}
        confirmLabel="Delete project"
        danger
        onCancel={() => setConfirm(false)}
        onConfirm={() => void onDelete()}
      />
    </div>
  );
}
